import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

interface HeaderProps {
  onOpenNotiDlg: () => void;
}
export default function Header({ onOpenNotiDlg }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, updateUserData, logout, notifications } = useAuth();
  const location = useLocation();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    // Fetch immediately on mount
    updateUserData();

    // Set up polling every 5 seconds
    intervalRef.current = setInterval(updateUserData, 5000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // Empty dependency array - runs once on mount

  const getLinkClass = (path) => {
    const baseClass = "transition-colors text-sm cursor-pointer";
    return isActive(path)
      ? `text-[#FFD349] font-semibold ${baseClass}`
      : `text-white/70 hover:text-white ${baseClass}`;
  };

  return (
    <header className="bg-[#1a1c24] border-b border-[#FFD349]/20 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo and Balance Section */}
          <div className="flex flex-col gap-1">
            <Link to="/" className="flex items-center gap-3">
              <h1 className="flex items-center gap-2">
                <div className="relative w-10 h-10 flex items-center justify-center">
                  <img
                    src="https://public.readdy.ai/ai/img_res/aa29cde3-a1c3-46be-b8a4-d1063c9a3299.jpg"
                    alt="DIGITWISE Logo"
                    className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(239,191,101,0.4)]"
                  />
                </div>
                <span className="text-3xl font-bold bg-gradient-to-r from-[#FFD349] via-[#FFC107] to-[#FF9800] bg-clip-text text-transparent" style={{
                  fontFamily: 'Orbitron, sans-serif'
                }}>
                  DIGITWISE
                </span>
              </h1>
            </Link>

            {/* Mobile Balance - Below Logo */}
            {isAuthenticated && user && (
              <div className="md:hidden flex items-center justify-center gap-1 bg-[#0D0E11] border border-[#FFD349]/20 rounded px-3 py-1.5 mx-auto w-fit">
                <i className="ri-wallet-3-line text-[#FFD349] text-sm"></i>
                <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  ${user.balance.toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link to="/" className={getLinkClass("/")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Home
            </Link>
            {/* <Link to="/promotions" className={getLinkClass("/promotions")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Promotions
            </Link> */}
            {/* <Link to="/vip" className={getLinkClass("/vip")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
              VIP
            </Link> */}
            {isAuthenticated && (
              <>
                {/* <Link to="/dashboard" className={getLinkClass("/dashboard")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Dashboard
                </Link> */}
                <Link to="/wallet" className={getLinkClass("/wallet")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Wallet
                </Link>
                <Link to="/support" className={getLinkClass("/support")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Support
                </Link>
                {/* <Link to="/profile" className={getLinkClass("/profile")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Profile
                </Link> */}
                <div className='text-white/70 hover:text-white transition-colors text-sm cursor-pointer' onClick={onOpenNotiDlg}>Notification
                  {(notifications > 0) && (<span className="text-xs font-semibold ml-1 text-white bg-red-600 rounded-full"
                    style={{ padding: '2px 6px 3px 6px' }}>
                    {(notifications > 10 ? '10+' : notifications)}
                  </span>)}
                </div>
              </>
            )}
          </nav>

          {/* Right Section */}
          <div className="hidden lg:flex items-center gap-4">
            {isAuthenticated && user ? (
              <>
                <div className="bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-2">
                  <span className="text-white/60 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>Balance</span>
                  <p className="text-[#FFD349] font-bold text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    ${user?.balance.toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={logout}
                  className="text-white/70 hover:text-red-400 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-logout-box-line text-lg"></i>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-white/70 hover:text-white transition-colors text-sm whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black font-bold py-2 px-6 rounded-lg text-sm whitespace-nowrap cursor-pointer hover:shadow-lg hover:shadow-[#FFD349]/30 transition-all"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:text-[#FFD349] transition-colors cursor-pointer p-2 bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-[#FFD349]/20 pt-4">
            <nav className="flex flex-col gap-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className={getLinkClass("/")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Home
              </Link>
              <Link to="/promotions" onClick={() => setMobileMenuOpen(false)} className={getLinkClass("/promotions")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Promotions
              </Link>
              <Link to="/support" onClick={() => setMobileMenuOpen(false)} className={getLinkClass("/support")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Support
              </Link>
              <Link to="/vip" onClick={() => setMobileMenuOpen(false)} className={getLinkClass("/vip")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                VIP
              </Link>
              <Link to="/wallet" onClick={() => setMobileMenuOpen(false)} className={getLinkClass("/wallet")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Wallet
              </Link>

              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className={getLinkClass("/dashboard")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Dashboard
                  </Link>
                  <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className={getLinkClass("/profile")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Profile
                  </Link>
                  <button
                    onClick={logout}
                    className="text-red-400 text-sm text-left cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className={getLinkClass("/login")} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Login
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black font-bold py-2 px-4 rounded-lg text-sm text-center cursor-pointer whitespace-nowrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Sign Up
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}